import { APP_INITIALIZER, Provider } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AUTH_CONFIG } from './tokens/auth-config.token';
import { AuthConfig } from './models/auth-config.model';
import { IPublicClientApplication, PopupRequest, SilentRequest } from '@azure/msal-browser';
import { isInIframe } from './iframe.util';

/**
 * APP_INITIALIZER that runs only when the app is loaded inside an iframe and a `sid` query parameter is present.
 *
 * Behavior (cross-origin iframe per MSAL docs):
 * - Try `ssoSilent({ sid, prompt: 'none' })` to establish a session and set the active account without interaction.
 * - If silent SSO fails (e.g., 3rd-party cookies blocked), fall back to `loginPopup` (no full-frame redirect).
 * - Never perform an interactive redirect inside the iframe.
 *
 * Reference: https://learn.microsoft.com/en-us/entra/msal/javascript/browser/iframe-usage#apps-with-cross-origin
 */
export function iframeSsoInitializerFactory(msal: MsalService, authConfig: AuthConfig) {
	return async () => {
		try {
			const inIframe = isInIframe();
			if (!inIframe) {
				return;
			}

			const urlParams = new URLSearchParams(window.location.search);
			const sid = urlParams.get('sid');
			if (!sid) {
				return; // No hint provided → do nothing; route guards or parent messaging can handle
			}

			const instance: IPublicClientApplication = msal.instance;

			// Attempt silent SSO with session id; this issues an auth code flow in a hidden iframe.
			const silentReq: SilentRequest = { sid, prompt: 'none' } as any;
			const ssoResponse = await instance.ssoSilent(silentReq);
			if (ssoResponse?.account) {
				instance.setActiveAccount(ssoResponse.account);
				return;
			}

			// If no account returned but no error thrown, continue to popup fallback below
		} catch (err) {
			// Ignore here and continue to popup fallback below
		}

		// Popup fallback inside iframe (only): request the union of configured API scopes.
		try {
			const allScopes: string[] = [
				...Object.values(authConfig.msalConfig.apis).flatMap((api) => api.scopes),
			];

			const popupReq: PopupRequest = {
				scopes: allScopes,
			} as PopupRequest;

			const popupResponse = await msal.instance.loginPopup(popupReq);
			if (popupResponse?.account) {
				msal.instance.setActiveAccount(popupResponse.account);
			}
		} catch (popupErr) {
			// If popup is blocked or user cancels, we leave the route protection to guards/UI
			// Optional: postMessage to parent to initiate top-level login
			// window.parent?.postMessage({ type: 'AGILOX_IFRAME_NEEDS_LOGIN' }, '*');
		}
	};
}

export function provideMsalIframeSsoInitializer(): Provider {
	return {
		provide: APP_INITIALIZER,
		useFactory: iframeSsoInitializerFactory,
		deps: [MsalService, AUTH_CONFIG],
		multi: true,
	};
}
